import {
    addDays,
    addMonths,
    addYears,
    differenceInCalendarDays,
    endOfYear,
    format,
    parseISO,
} from "date-fns";
import { Calendar } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useMemberProfile } from "../../MemberProfileContext";
import { ProfileParagraph } from "../../profilePrimitives";
import { DateField } from "../FormFields";
import { FormShell } from "../ProfileOverlayShell";
import type { FormProps } from "../types";

const CUSTOM_CHOICE = "custom";

export function ExtendTrialForm({ onClose }: FormProps) {
    const { profile, extendTrial } = useMemberProfile();
    const currentEnd = profile.membership.trialEndsOn
        ? parseISO(profile.membership.trialEndsOn)
        : new Date();
    const presets = extendTrialPresets(currentEnd);
    const [choice, setChoice] = useState(presets[1]?.label ?? CUSTOM_CHOICE);
    const [customDate, setCustomDate] = useState<Date | undefined>();
    const custom = choice === CUSTOM_CHOICE;
    const selectedDate = custom
        ? customDate
        : presets.find((option) => option.label === choice)?.date;
    const sameDay =
        selectedDate != null &&
        differenceInCalendarDays(selectedDate, currentEnd) === 0;

    function selectChoice(next: string) {
        setChoice(next);
        if (next === CUSTOM_CHOICE) setCustomDate(undefined);
    }

    return (
        <FormShell
            title="Extend trial"
            subtitle={`Currently ends ${format(currentEnd, "d MMM yyyy")}`}
            saveLabel="Extend"
            onClose={onClose}
            saveDisabled={!selectedDate || sameDay}
            onSave={() => {
                if (!selectedDate || sameDay) return;
                extendTrial(
                    {
                        extendByDays: differenceInCalendarDays(
                            selectedDate,
                            currentEnd,
                        ),
                    },
                    onClose,
                );
            }}
        >
            <div
                role="radiogroup"
                aria-label="Trial extension"
                className="flex flex-col gap-1"
            >
                {presets.map((option) => (
                    <TrialOption
                        key={option.label}
                        selected={choice === option.label}
                        onSelect={() => selectChoice(option.label)}
                    >
                        <span className="min-w-0 flex-1 text-left">{option.label}</span>
                        <span className="shrink-0 font-normal text-muted-foreground text-xs">
                            {format(option.date, "d MMM yyyy")}
                        </span>
                    </TrialOption>
                ))}
                <TrialOption
                    selected={custom}
                    onSelect={() => selectChoice(CUSTOM_CHOICE)}
                >
                    <Calendar data-icon="inline-start" />
                    <span className="min-w-0 flex-1 text-left">
                        Until specific date
                    </span>
                    {customDate && (
                        <span className="shrink-0 font-normal text-muted-foreground text-xs">
                            {format(customDate, "d MMM yyyy")}
                        </span>
                    )}
                </TrialOption>
            </div>
            {custom && (
                <DateField
                    id="trial-end-custom"
                    label="Trial end date"
                    date={selectedDate}
                    endMonth={endOfYear(addYears(currentEnd, 1))}
                    onDateChange={setCustomDate}
                    error={
                        sameDay
                            ? `Pick a date other than the current trial end date (${format(currentEnd, "d MMM yyyy")})`
                            : undefined
                    }
                />
            )}
            {!custom && sameDay && (
                <ProfileParagraph muted>
                    Pick a date other than the current trial end date (
                    {format(currentEnd, "d MMM yyyy")}).
                </ProfileParagraph>
            )}
        </FormShell>
    );
}

function TrialOption({
    selected,
    onSelect,
    children,
}: {
    selected: boolean;
    onSelect: () => void;
    children: React.ReactNode;
}) {
    return (
        <Button
            type="button"
            variant="outline"
            role="radio"
            aria-checked={selected}
            onClick={onSelect}
            className={cn(
                "h-auto w-full justify-between whitespace-normal rounded-md px-3 py-2.5 text-left font-normal",
                selected && "bg-muted",
            )}
        >
            {children}
        </Button>
    );
}

function extendTrialPresets(from: Date) {
    return [
        { label: "2 weeks", date: addDays(from, 14) },
        { label: "1 month", date: addMonths(from, 1) },
        { label: "2 months", date: addMonths(from, 2) },
        { label: "3 months", date: addMonths(from, 3) },
        { label: "6 months", date: addMonths(from, 6) },
        { label: "1 year", date: addYears(from, 1) },
    ];
}
